import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Slider,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Card,
  CardMedia,
  CardActions,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Image as ImageIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  ImageGenerationService,
  ImageProvider,
  ImageModel,
  ImageSize,
  ImageGenerationRequest,
  ImageGenerationResponse,
  ProviderValidation,
} from '../services/imageGenerationService';
import { ImagePromptService } from '../services/imagePromptService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { AssetRetrievalService, GeneratedAsset } from '../services/assetRetrievalService';

interface ImageGenerationPanelProps {
  initialPrompt?: string;
  onPromptChange?: (prompt: string) => void;
  onImagesGenerated?: (images: any[]) => void;
  onPromptSaved?: () => void;
  sessionId?: string; // NEW: Optional sessionId for session-based generation with auto-persistence
  sessionType?: 'ImagePromptSession' | 'PromptEvolutionSession' | 'SocialMediaSession' | 'FashionSession'; // NEW: Session type for context
}

const ImageGenerationPanel: React.FC<ImageGenerationPanelProps> = ({
  initialPrompt = '',
  onPromptChange,
  onImagesGenerated,
  onPromptSaved,
  sessionId,
  sessionType
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // State
  const [providers, setProviders] = useState<ImageProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [prompt, setPrompt] = useState(initialPrompt);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [numImages, setNumImages] = useState(1);
  const [seed, setSeed] = useState<number | undefined>(undefined);
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [style, setStyle] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedImagesList, setGeneratedImagesList] = useState<ImageGenerationResponse[]>([]);
  const [validationDialog, setValidationDialog] = useState(false);
  const [validationResults, setValidationResults] = useState<ProviderValidation[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [backendAssets, setBackendAssets] = useState<GeneratedAsset[]>([]);
  const [backendLoading, setBackendLoading] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);

  // Fetch session-specific assets when sessionId is provided
  const fetchSessionAssets = async () => {
    if (!sessionId) {
      console.log('[ImageGenerationPanel] No sessionId provided, skipping asset fetch');
      return;
    }

    setBackendLoading(true);
    setBackendError(null);

    try {
      console.log(`[ImageGenerationPanel] Fetching assets for session: ${sessionId}`);
      const assets = await AssetRetrievalService.getSessionAssets(sessionId);
      console.log(`[ImageGenerationPanel] Fetched ${assets.length} assets for session ${sessionId}`);
      setBackendAssets(assets);
    } catch (err: any) {
      console.error('[ImageGenerationPanel] Error fetching session assets:', err);
      setBackendError(err.message || 'Failed to fetch session assets');
      setBackendAssets([]);
    } finally {
      setBackendLoading(false);
    }
  };

  // Refetch backend data function
  const refetchBackendData = () => {
    if (sessionId) {
      fetchSessionAssets();
    }
  };

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, []);

  // Fetch session assets when sessionId changes
  useEffect(() => {
    if (sessionId) {
      fetchSessionAssets();
    }
  }, [sessionId]);

  // Update prompt when initialPrompt changes
  useEffect(() => {
    setPrompt(initialPrompt);
  }, [initialPrompt]);

  // Merge backend assets with local data
  useEffect(() => {
    if (backendAssets && backendAssets.length > 0) {
      console.log(`[ImageGenerationPanel] Processing ${backendAssets.length} backend assets for session: ${sessionId || 'all'}`);

      const converted: ImageGenerationResponse[] = backendAssets
        .map((asset, index) => {
          // Parse metadataJson if it exists
          let parsedMetadata = asset.metadata || {};
          if (asset.metadataJson && typeof asset.metadataJson === 'string') {
            try {
              parsedMetadata = { ...parsedMetadata, ...JSON.parse(asset.metadataJson) };
            } catch (error) {
              console.warn('[ImageGenerationPanel] Failed to parse metadataJson:', error);
            }
          }

          // Extract image URL from multiple possible locations
          // Priority: assetUrl > metadata.url > metadata.imageUrl > storageUrl > mediaUrl > textContent
          let imageUrl = asset.assetUrl || parsedMetadata.url || parsedMetadata.imageUrl || asset.storageUrl || asset.mediaUrl || asset.textContent;

          // Enhanced logging for debugging
          console.log(`[ImageGenerationPanel] Asset ${index + 1}/${backendAssets.length}:`, {
            id: asset.id,
            sessionId: asset.sessionId,
            assetType: asset.assetType,
            assetUrl: asset.assetUrl,
            hasAssetUrl: !!asset.assetUrl,
            hasStorageUrl: !!asset.storageUrl,
            hasMediaUrl: !!asset.mediaUrl,
            hasTextContent: !!asset.textContent,
            hasMetadataJson: !!asset.metadataJson,
            parsedMetadata: parsedMetadata,
            hasMetadataUrl: !!(parsedMetadata.imageUrl || parsedMetadata.url),
            extractedUrl: imageUrl ? imageUrl.substring(0, 80) + '...' : null
          });

          // Validate that imageUrl has a value - skip if null or empty
          if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
            console.warn('[ImageGenerationPanel] Asset has no image URL - skipping:', {
              id: asset.id,
              sessionId: asset.sessionId,
              assetType: asset.assetType,
              imageUrl: imageUrl
            });
            return null;
          }

          // Trim URL
          const trimmedUrl = imageUrl.trim();

          // Validate URL safety - check if it can be decoded without errors
          try {
            // Test if URL contains characters that would cause decodeURI to fail
            if (trimmedUrl.includes('%')) {
              // Try to decode to check if it's valid
              decodeURI(trimmedUrl);
            }
          } catch (uriError) {
            console.warn('[ImageGenerationPanel] Invalid/malformed URL - skipping:', {
              id: asset.id,
              sessionId: asset.sessionId,
              url: trimmedUrl.substring(0, 100),
              error: uriError instanceof Error ? uriError.message : 'Unknown error'
            });
            return null;
          }

          console.log('[ImageGenerationPanel] Image URL found:', {
            id: asset.id,
            url: trimmedUrl.substring(0, 80) + '...',
            urlLength: trimmedUrl.length
          });

          const converted = {
            model: parsedMetadata.model || asset.model || '',
            images: [{
              url: trimmedUrl, // Use trimmed URL
              contentType: parsedMetadata.contentType || 'image/png',
              metadata: parsedMetadata
            }],
            metadata: {
              ...parsedMetadata,
              assetId: asset.id,
              sessionId: asset.sessionId,
              promptUsed: asset.promptUsed || parsedMetadata.prompt,
              seed: asset.seed || parsedMetadata.seed,
              provider: asset.provider || parsedMetadata.provider,
            }
          };

          console.log('[ImageGenerationPanel] Successfully converted asset:', {
            assetId: asset.id,
            imageUrl: trimmedUrl.substring(0, 80) + '...',
            model: converted.model,
            seed: converted.metadata.seed,
            provider: converted.metadata.provider
          });

          return converted;
        })
        .filter(Boolean) as ImageGenerationResponse[]; // Remove nulls

      console.log(`[ImageGenerationPanel] Converted ${converted.length} assets to images`);

      setGeneratedImagesList(prev => {
        const newImages = converted.filter(conv => {
          // Dedup based on assetId or prompt/model combo
          const isDuplicate = prev.some(existing =>
            (existing.metadata?.assetId && existing.metadata.assetId === conv.metadata?.assetId) ||
            (existing.model === conv.model && JSON.stringify(existing.metadata?.prompt) === JSON.stringify(conv.metadata?.prompt))
          );

          if (isDuplicate) {
            console.log('[ImageGenerationPanel] Skipping duplicate asset:', conv.metadata?.assetId);
          }

          return !isDuplicate;
        });

        console.log(`[ImageGenerationPanel] Adding ${newImages.length} new images to list (${prev.length} existing)`);
        return [...newImages, ...prev];
      });
    } else if (backendAssets && backendAssets.length === 0) {
      console.log('[ImageGenerationPanel] No backend assets found for session');
    }
  }, [backendAssets]); // Assets are already session-filtered from API

  const loadProviders = async () => {
    try {
      setProvidersLoading(true);
      setError(null);
      
      const allProviders = await ImageGenerationService.getProviders();
      console.log('All providers from API:', allProviders); // Debug log
      
      // For now, just exclude Gemini/Google providers and show everything else
      // This ensures we don't accidentally filter out all providers
      const filteredProviders = allProviders.filter(provider => {
        const providerNameLower = provider.name.toLowerCase();
        // Exclude Gemini and Google providers
        return !providerNameLower.includes('gemini') && 
               !providerNameLower.includes('google');
      });
      
      console.log('Filtered providers (excluding Gemini):', filteredProviders); // Debug log
      
      // Always set providers, even if empty array
      setProviders(filteredProviders);
      
      // Select first available model if any
      if (filteredProviders.length > 0 && filteredProviders[0].models.length > 0) {
        setSelectedModel(filteredProviders[0].models[0].modelId);
      }
      
      // If no providers after filtering, log warning
      if (filteredProviders.length === 0) {
        console.warn('No image generation providers available after filtering');
        setError('No image generation providers available. Please ensure the backend server is running on http://localhost:5116 and the image generation service is properly configured.');
      }
    } catch (err) {
      setError('Failed to load image generation providers. Please check your connection and try again.');
      console.error('Error loading providers:', err);
      // Set empty providers array on error
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  };

  const getSelectedModelInfo = (): ImageModel | null => {
    for (const provider of providers) {
      const model = provider.models.find(m => m.modelId === selectedModel);
      if (model) return model;
    }
    return null;
  };

  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId);
    
    // Adjust size to match model capabilities
    const model = providers
      .flatMap(p => p.models)
      .find(m => m.modelId === modelId);
      
    if (model && model.supportedSizes.length > 0) {
      const bestSize = ImageGenerationService.findBestSizeMatch(model, width, height);
      if (bestSize) {
        setWidth(bestSize.width);
        setHeight(bestSize.height);
      }
    }
  };

  const handleSizePreset = (size: ImageSize) => {
    setWidth(size.width);
    setHeight(size.height);
  };

  const generateImages = async () => {
    if (!prompt.trim() || !selectedModel) return;

    setLoading(true);
    setError(null);

    try {
      let response: ImageGenerationResponse;

      // Use session-based generation with auto-persistence if sessionId is provided
      if (sessionId && sessionType === 'ImagePromptSession') {
        console.log('[ImageGenerationPanel] Using session-based generation:', {
          sessionId,
          sessionType,
          model: selectedModel
        });

        const sessionResponse = await ImagePromptService.generateImageForSession(sessionId, {
          model: selectedModel,
          width,
          height,
          numImages,
          seed,
          guidanceScale,
          negativePrompt: negativePrompt.trim() || undefined,
        });

        console.log('[ImageGenerationPanel] Session-based generation response:', {
          model: sessionResponse.model,
          imageCount: sessionResponse.images?.length || 0,
          assetId: sessionResponse.assetId,
          hasImages: !!sessionResponse.images,
          firstImageUrl: sessionResponse.images?.[0]?.url?.substring(0, 50) + '...',
          fullResponse: sessionResponse
        });

        // Convert session response to ImageGenerationResponse format
        response = {
          model: sessionResponse.model,
          images: sessionResponse.images,
          metadata: {
            ...sessionResponse.metadata,
            assetId: sessionResponse.assetId, // Asset ID from auto-persistence
            sessionId: sessionId,
            sessionType: sessionType,
          },
        };

        console.log('[ImageGenerationPanel] Converted response for display:', {
          model: response.model,
          imageCount: response.images?.length || 0,
          assetId: response.metadata?.assetId
        });
      } else {
        console.log('[ImageGenerationPanel] Using legacy direct generation (no session)');

        // Standard generation without session (legacy mode)
        const request: ImageGenerationRequest = {
          model: selectedModel,
          prompt: prompt.trim(),
          width,
          height,
          numImages,
        };

        // Add optional parameters
        if (negativePrompt.trim()) {
          request.negativePrompt = negativePrompt.trim();
        }
        if (seed !== undefined) {
          request.seed = seed;
        }
        if (guidanceScale !== undefined) {
          request.guidanceScale = guidanceScale;
        }
        if (style) {
          request.style = style;
        }

        response = await ImageGenerationService.generateImage(request);

        console.log('[ImageGenerationPanel] Legacy generation response:', {
          model: response.model,
          imageCount: response.images?.length || 0
        });
      }

      console.log('[ImageGenerationPanel] Adding generated images to list');

      // Append new images to the list instead of replacing
      setGeneratedImagesList(prev => [...prev, response]);

      // Call the callback if provided
      if (onImagesGenerated) {
        onImagesGenerated(response.images);
      }

      // Refetch backend data if using session-based generation
      if (sessionId) {
        console.log('[ImageGenerationPanel] Refetching backend data after generation...');
        refetchBackendData();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to generate images';
      setError(errorMessage);
      console.error('[ImageGenerationPanel] Image generation error:', {
        message: errorMessage,
        status: err.response?.status,
        data: err.response?.data,
        fullError: err
      });
    } finally {
      setLoading(false);
    }
  };

  const validateProviders = async () => {
    try {
      const results = await ImageGenerationService.validateProviders();
      setValidationResults(results);
      setValidationDialog(true);
    } catch (err) {
      setError('Failed to validate providers');
      console.error(err);
    }
  };

  const downloadImage = async (imageUrl: string, index: number) => {
    await ImageGenerationService.downloadImage(
      imageUrl,
      `${selectedModel}_${Date.now()}_${index}.png`
    );
  };

  const modelInfo = getSelectedModelInfo();

  // Save current prompt to local storage
  const savePrompt = () => {
    if (!prompt.trim()) return;
    
    // Get existing prompts
    const saved = localStorage.getItem('image_generation_prompts');
    let existingPrompts = [];
    if (saved) {
      try {
        existingPrompts = JSON.parse(saved);
      } catch (err) {
        console.error('Error parsing saved prompts:', err);
      }
    }
    
    // Add new prompt
    const newPrompt = {
      id: `prompt-${Date.now()}`,
      prompt: prompt.trim(),
      timestamp: new Date(),
      model: selectedModel
    };
    
    const updatedPrompts = [newPrompt, ...existingPrompts];
    localStorage.setItem('image_generation_prompts', JSON.stringify(updatedPrompts));
    
    // Notify parent
    if (onPromptSaved) {
      onPromptSaved();
    }
  };

  return (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: { xs: 1.5, sm: 2 },
          gap: { xs: 1.5, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <ImageIcon sx={{ mr: 1, fontSize: { xs: 24, sm: 28 } }} color="primary" />
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
            >
              {isMobile ? 'Image Gen' : 'Image Generation'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={isMobile ? "" : "Validate Provider Configurations"}>
              <IconButton 
                onClick={validateProviders} 
                size="small"
                sx={{ minHeight: 36, minWidth: 36 }}
              >
                <SettingsIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={isMobile ? "" : "Refresh Providers"}>
              <IconButton 
                onClick={loadProviders} 
                size="small"
                sx={{ minHeight: 36, minWidth: 36 }}
              >
                <RefreshIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Model Selection */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
          <FormControl fullWidth>
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Model
            </InputLabel>
            <Select
              value={selectedModel}
              onChange={(e) => handleModelChange(e.target.value)}
              label="Model"
              disabled={providersLoading || providers.length === 0}
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
                {providersLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {isMobile ? 'Loading...' : 'Loading providers...'}
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : providers.length === 0 ? (
                  <MenuItem disabled>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? 'No providers' : 'No image generation providers available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  providers.map(provider => (
                    <MenuItem key={provider.name} disabled>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        {provider.name}
                      </Typography>
                    </MenuItem>
                  )).concat(
                    providers.flatMap(provider => 
                      provider.models.map(model => (
                        <MenuItem key={`${provider.name}:${model.modelId}`} value={model.modelId}>
                          <Box sx={{ ml: 2 }}>
                            <Typography 
                              variant="body2"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              {model.displayName}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {isMobile && model.description.length > 30 
                                ? `${model.description.substring(0, 30)}...` 
                                : model.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )
                  )
                )}
            </Select>
          </FormControl>

          {/* Prompt */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Prompt"
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                onPromptChange?.(e.target.value);
              }}
              placeholder={isMobile ? "Describe the image..." : "Describe the image you want to generate..."}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <Tooltip title={isMobile ? "" : "Save this prompt"}>
              <IconButton 
                onClick={savePrompt}
                disabled={!prompt.trim()}
                sx={{ mt: 1, minHeight: 44, minWidth: 44 }}
              >
                <SaveIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Error Alert */}
          {error && !providersLoading && (
            <Alert 
              severity="error" 
              action={
                <Button 
                  size="small" 
                  onClick={loadProviders}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Retry
                </Button>
              }
              sx={{
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Model Capabilities Info */}
          {modelInfo && (
            <Alert 
              severity="info" 
              icon={<InfoIcon />}
              sx={{
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            >
              <Box>
                <Typography 
                  variant="body2"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <strong>{isMobile && modelInfo.displayName.length > 25 
                    ? `${modelInfo.displayName.substring(0, 25)}...` 
                    : modelInfo.displayName}</strong> {isMobile ? '' : 'capabilities:'}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {modelInfo.capabilities.supportsNegativePrompt && (
                    <Chip 
                      label={isMobile ? "Neg Prompt" : "Negative Prompt"} 
                      size="small" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {modelInfo.capabilities.supportsSeed && (
                    <Chip 
                      label={isMobile ? "Seed" : "Seed Control"} 
                      size="small" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {modelInfo.capabilities.supportsGuidanceScale && (
                    <Chip 
                      label={isMobile ? "Guidance" : "Guidance Scale"} 
                      size="small" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {modelInfo.capabilities.supportsStyle && (
                    <Chip 
                      label="Styles" 
                      size="small" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {modelInfo.capabilities.supportsInpainting && (
                    <Chip 
                      label="Inpainting" 
                      size="small" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                </Box>
                {modelInfo.pricing && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      mt: 1, 
                      display: 'block',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }}
                  >
                    Price: ${modelInfo.pricing.pricePerImage} {modelInfo.pricing.currency} per image
                  </Typography>
                )}
              </Box>
            </Alert>
          )}

          {/* Size Presets */}
          {modelInfo && modelInfo.supportedSizes.length > 0 && (
            <Box>
              <Typography 
                variant="body2" 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {isMobile ? 'Sizes:' : 'Size Presets:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {modelInfo.supportedSizes.map((size, index) => (
                  <Chip
                    key={index}
                    label={isMobile 
                      ? `${size.width}×${size.height}` 
                      : `${size.width}×${size.height} (${size.aspectRatio})`}
                    onClick={() => handleSizePreset(size)}
                    color={width === size.width && height === size.height ? 'primary' : 'default'}
                    variant={width === size.width && height === size.height ? 'filled' : 'outlined'}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Advanced Options */}
          <Box>
            <Button
              onClick={() => setAdvancedOpen(!advancedOpen)}
              endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 36
              }}
            >
              {isMobile ? 'Advanced' : 'Advanced Options'}
            </Button>
            <Collapse in={advancedOpen}>
              <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                {/* Negative Prompt */}
                {modelInfo?.capabilities.supportsNegativePrompt && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Negative Prompt"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder={isMobile ? "What to avoid..." : "What to avoid in the image..."}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                )}

                {/* Image Size */}
                <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 } }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Width"
                    value={width}
                    onChange={(e) => setWidth(parseInt(e.target.value) || 512)}
                    inputProps={{ min: 64, max: 2048, step: 64 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Height"
                    value={height}
                    onChange={(e) => setHeight(parseInt(e.target.value) || 512)}
                    inputProps={{ min: 64, max: 2048, step: 64 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                </Box>

                {/* Number of Images */}
                <Box>
                  <Typography 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {isMobile ? `Images: ${numImages}` : `Number of Images: ${numImages}`}
                  </Typography>
                  <Slider
                    value={numImages}
                    onChange={(_, value) => setNumImages(value as number)}
                    min={1}
                    max={4}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>

                {/* Seed */}
                {modelInfo?.capabilities.supportsSeed && (
                  <TextField
                    fullWidth
                    type="number"
                    label={isMobile ? "Seed" : "Seed (optional)"}
                    value={seed || ''}
                    onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder={isMobile ? "Random seed" : "Random seed for reproducibility"}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                )}

                {/* Guidance Scale */}
                {modelInfo?.capabilities.supportsGuidanceScale && (
                  <Box>
                    <Typography 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? `Guidance: ${guidanceScale}` : `Guidance Scale: ${guidanceScale}`}
                    </Typography>
                    <Slider
                      value={guidanceScale}
                      onChange={(_, value) => setGuidanceScale(value as number)}
                      min={1}
                      max={20}
                      step={0.5}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                )}

                {/* Style */}
                {modelInfo?.capabilities.supportsStyle && modelInfo.capabilities.supportedStyles && (
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Style
                    </InputLabel>
                    <Select
                      value={style}
                      onChange={(e) => setStyle(e.target.value)}
                      label="Style"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <em>None</em>
                      </MenuItem>
                      {modelInfo.capabilities.supportedStyles.map((s) => (
                        <MenuItem key={s} value={s} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {s}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Collapse>
          </Box>

          {/* Generate Button */}
          <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={generateImages}
              disabled={loading || !prompt.trim() || !selectedModel}
              startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <ImageIcon />)}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 48
              }}
            >
              {loading ? 'Generating...' : (isMobile ? 'Generate' : 'Generate Images')}
            </Button>
        </Box>

        {/* Error Display for generation errors */}
        {error && providersLoading === false && providers.length > 0 && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: { xs: 1.5, sm: 2 },
              '& .MuiAlert-message': {
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Generated Images */}
        {generatedImagesList.length > 0 && (
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              mb: { xs: 1.5, sm: 2 },
              gap: { xs: 1.5, sm: 0 }
            }}>
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {isMobile
                  ? `Images (${generatedImagesList.reduce((acc, resp) => acc + (resp.images?.length || 0), 0)})`
                  : `Generated Images (${generatedImagesList.reduce((acc, resp) => acc + (resp.images?.length || 0), 0)})`}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setGeneratedImagesList([])}
                startIcon={!isMobile && <DeleteIcon />}
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minHeight: 36
                }}
              >
                {isMobile ? 'Clear' : 'Clear All'}
              </Button>
            </Box>
            <Box sx={{ 
              maxHeight: { xs: '60vh', sm: '80vh' },
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1
            }}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {generatedImagesList.map((response, responseIndex) =>
                  (response.images || []).map((image, imageIndex) => {
                    const globalIndex = generatedImagesList
                      .slice(0, responseIndex)
                      .reduce((acc, resp) => acc + (resp.images?.length || 0), 0) + imageIndex;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={`${responseIndex}-${imageIndex}`}>
                        <Card>
                          <CardMedia
                            component="img"
                            image={image.url}
                            alt={`Generated image ${globalIndex + 1}`}
                            sx={{ 
                              height: { xs: 200, sm: 300 },
                              objectFit: 'cover',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('[ImageGenerationPanel] Opening image in new tab:', image.url);
                              if (image.url) {
                                // Try window.open first
                                const newWindow = window.open(image.url, '_blank', 'noopener,noreferrer');

                                // If blocked by popup blocker, use anchor fallback
                                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                                  console.warn('[ImageGenerationPanel] window.open was blocked, using anchor fallback');
                                  const link = document.createElement('a');
                                  link.href = image.url;
                                  link.target = '_blank';
                                  link.rel = 'noopener noreferrer';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              } else {
                                console.error('[ImageGenerationPanel] Image URL is null/undefined');
                              }
                            }}
                          />
                          <CardActions sx={{ justifyContent: 'space-between', p: { xs: 1, sm: 1.5 } }}>
                            <Button
                              size="small"
                              startIcon={!isMobile && <DownloadIcon />}
                              onClick={() => {
                                console.log('[ImageGenerationPanel] Downloading image:', image.url);
                                downloadImage(image.url, globalIndex);
                              }}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              {isMobile ? 'Save' : 'Download'}
                            </Button>
                            <Tooltip title={isMobile ? "" : "Open in new tab"}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('[ImageGenerationPanel] Icon click - Opening image:', {
                                    url: image.url,
                                    urlType: typeof image.url,
                                    urlLength: image.url?.length,
                                    fullImage: image
                                  });
                                  if (image.url) {
                                    // Try window.open first
                                    const newWindow = window.open(image.url, '_blank', 'noopener,noreferrer');

                                    // If blocked by popup blocker, use anchor fallback
                                    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                                      console.warn('[ImageGenerationPanel] window.open was blocked, using anchor fallback');
                                      const link = document.createElement('a');
                                      link.href = image.url;
                                      link.target = '_blank';
                                      link.rel = 'noopener noreferrer';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }
                                  } else {
                                    console.error('[ImageGenerationPanel] Image URL is null/undefined');
                                  }
                                }}
                                sx={{ minHeight: 36, minWidth: 36 }}
                              >
                                <OpenInNewIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>
                          </CardActions>
                          {image.metadata && (
                            <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1 }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              >
                                {isMobile && response.model.length > 20 
                                  ? `${response.model.substring(0, 20)}...` 
                                  : `Model: ${response.model}`}
                              </Typography>
                            </Box>
                          )}
                        </Card>
                      </Grid>
                    );
                  })
                )}
              </Grid>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Validation Dialog */}
      <Dialog
        open={validationDialog}
        onClose={() => setValidationDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {isMobile ? 'Provider Status' : 'Provider Configuration Status'}
        </DialogTitle>
        <DialogContent>
          {validationResults.map((result, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {result.valid ? (
                <CheckIcon color="success" sx={{ mr: 1, fontSize: { xs: 20, sm: 24 } }} />
              ) : (
                <ErrorIcon color="error" sx={{ mr: 1, fontSize: { xs: 20, sm: 24 } }} />
              )}
              <Typography 
                variant="body2" 
                sx={{ 
                  flex: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {result.provider}
              </Typography>
              {result.error && (
                <Typography 
                  variant="caption" 
                  color="error"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                >
                  {isMobile && result.error.length > 15 
                    ? `${result.error.substring(0, 15)}...` 
                    : result.error}
                </Typography>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setValidationDialog(false)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageGenerationPanel;